/**
 * Navigation config — the single source of truth for what appears in
 * the sidebar, extracted from the old 2,700-line SideNavbarLayout
 * monolith (2026-06 nav refresh).
 *
 * Pure data builders: no component state lives here. Accordion
 * expansion is keyed by `sectionKey` and owned by the layout (persisted
 * per-user). Icons are lucide-react (replacing the old inline SVGs).
 *
 * Staff IA (Dan 2026-10-31, consolidated from 18 rail items to 12 after
 * Accelimate's top level):
 *   (ungrouped)        Home (Dashboard, My Tasks)
 *   People             CRM (Members, {participants}, Leads & Contacts) ·
 *                      Attendance (its own: staff mark it all day)
 *   Programs & Events  Programs (+ {participant} training) · Events
 *   Commerce           Billing · Store · Marketing (+ Review Requests)
 *   Insights           Reports (single link → landing page)
 *   Organization       Team (Staff, HR) · Phone (Calls, AI Receptionist) ·
 *                      Settings (single link → landing)
 * Support left the rail: the top bar's Help opens the Knowledgebase, which
 * links to the Support Center.
 *
 * Merged sections are built by mergeSections from the old section configs;
 * every page keeps its old section_<key> gate (sectionPermission), so the
 * consolidation neither widens nor narrows what a staff role sees.
 *
 * Reports and Settings deliberately collapsed to single links — their
 * 20+/27-item accordions moved to landing pages with filtering.
 */
import React from "react";
import {
  LayoutDashboard,
  Users,
  Zap,
  ClipboardCheck,
  ListChecks,
  LayoutGrid,
  Calendar,
  ShoppingBag,
  Megaphone,
  Contact,
  Receipt,
  BarChart3,
  UserCog,
  Bot,
  PhoneCall,
  Briefcase,
  Settings as SettingsIcon,
} from "lucide-react";

export interface NavLeafItem {
  to: string;
  label: string;
  permission?: string;
  adminOnly?: boolean;
  ccStaffOnly?: boolean;
  moduleKey?: string;
  comingSoon?: boolean;
  /** the section_<key> permission this page needs (pages moved into a merged section keep their old section's gate) */
  sectionPermission?: string;
}

export interface NavCategoryItem {
  label: string;
  isCategory: true;
}

export interface NavNestedItem {
  label: string;
  isNested: true;
  /** expansion key in the layout's expanded-sections map */
  navKey: string;
  adminOnly?: boolean;
  items: NavLeafItem[];
  sectionPermission?: string;
}

export type NavItem = NavLeafItem | NavCategoryItem | NavNestedItem;

export interface NavSectionConfig {
  title: string;
  /** permission key (section_<key>) AND expansion-map key */
  sectionKey: string;
  icon?: React.ReactNode;
  directLink: string;
  /** renders as a plain link — no accordion */
  isDirectLink?: boolean;
  adminOnly?: boolean;
  moduleKey?: string;
  items: NavItem[];
  /** set during filtering when a hard-gated module isn't entitled */
  locked?: boolean;
  /** visible to every staff role — no section_<key> permission required */
  skipSectionPermission?: boolean;
}

export interface NavGroup {
  /** muted uppercase label; null renders no label */
  label: string | null;
  sections: NavSectionConfig[];
}

export interface ManageMenuContext {
  tenantId: string | null;
  participantSingular: string;
  participantPlural: string;
  /** tenant settings (localStorage mirror) — lms_enabled etc. */
  settings: any;
}

const iconCls = "w-[18px] h-[18px] flex-shrink-0";

interface SectionPart {
  /** page-list heading for this part's pages */
  label?: string;
  /** the old section the pages come from (its section_<key> gates them) */
  section: NavSectionConfig;
  /** a subset of the section's pages; default all of them */
  items?: NavItem[];
}

/**
 * One rail section made of several old ones. Each page carries its old
 * section's gate (none when that section skipped the section permission),
 * and the merged section skips the section permission itself, so a staff
 * role sees exactly the pages it saw before.
 */
function mergeSections(
  // skipSectionPermission is written in each literal (not added here) so
  // scripts/schema/check-nav-permissions.js, which reads this file as text,
  // sees that no section_<key> gates the merged section.
  target: Pick<NavSectionConfig, "title" | "sectionKey" | "icon" | "directLink" | "moduleKey"> & {
    skipSectionPermission: true;
  },
  parts: SectionPart[]
): NavSectionConfig {
  const items: NavItem[] = [];
  for (const part of parts) {
    const gate = part.section.skipSectionPermission ? undefined : part.section.sectionKey;
    if (part.label) items.push({ label: part.label, isCategory: true });
    for (const item of part.items ?? part.section.items) {
      items.push("isCategory" in item ? item : { ...item, sectionPermission: item.sectionPermission ?? gate });
    }
  }
  return { ...target, items };
}

/** A nested group's pages as plain pages (inside a part that already has a heading). */
const flattenNested = (items: NavItem[]): NavItem[] =>
  items.flatMap((item) => ("isNested" in item ? item.items : [item]));

export function buildManageMenuGroups(ctx: ManageMenuContext): NavGroup[] {
  const { tenantId, participantSingular, participantPlural, settings } = ctx;

  // Always-visible way back to the staff dashboard. The logo also
  // points at the user's configured home route, but a configurable
  // logo is not discoverable navigation (Dan got stranded off the
  // dashboard the day the nav shipped without this).
  const dashboard: NavSectionConfig = {
    title: "Dashboard",
    sectionKey: "dashboard",
    skipSectionPermission: true,
    icon: <LayoutDashboard className={iconCls} />,
    directLink: `/${tenantId}/manage/dashboard`,
    isDirectLink: true,
    items: [{ to: `/${tenantId}/manage/dashboard`, label: "Dashboard" }],
  };

  const memberships: NavSectionConfig = {
    title: "Members",
    sectionKey: "memberships",
    icon: <Users className={iconCls} />,
    directLink: `/${tenantId}/manage/memberships`,
    items: [
      {
        to: `/${tenantId}/manage/memberships/add`,
        label: "Add New Membership",
        permission: "add_membership_page",
      },
      {
        to: `/${tenantId}/manage/memberships`,
        label: "Manage Memberships",
        permission: "show_memberships_page",
      },
      {
        to: `/${tenantId}/manage/memberships/invoices`,
        label: "View Invoices",
        permission: "view_membership_invoices",
      },
      {
        to: `/${tenantId}/manage/memberships/reassign`,
        label: "Reassign Athletes",
        permission: "page_memberships_reassign",
      },
    ],
  };

  const athletes: NavSectionConfig = {
    title: participantPlural,
    sectionKey: "athletes",
    icon: <Zap className={iconCls} />,
    directLink: `/${tenantId}/manage/athletes`,
    items: [
      {
        to: `/${tenantId}/manage/athletes`,
        label: `Manage ${participantPlural}`,
        permission: "page_athletes",
      },
      {
        to: `/${tenantId}/manage/athletes/pending-reactivations`,
        label: "Pending Reactivations",
        permission: "manage_athletes_page",
      },
      { to: `/${tenantId}/manage/teams`, label: "Teams", permission: "page_teams" },
      { to: `/${tenantId}/manage/levels`, label: "Levels", permission: "page_levels" },
      ...(settings?.lms_enabled
        ? [
            {
              label: "Training",
              isNested: true as const,
              navKey: "athletes_lms",
              // Group visibility = union of its children (each child is
              // permission-gated; admins bypass via the leaf filter).
              items: [
                { to: `/${tenantId}/manage/lms`, label: "Training Overview", permission: "page_lms" },
                {
                  to: `/${tenantId}/manage/training/athletes`,
                  label: "Athlete Courses",
                  permission: "page_training_athletes",
                },
                {
                  to: `/${tenantId}/manage/lms/enrollments`,
                  label: "Enrollments",
                  permission: "page_lms_enrollments",
                },
                { to: `/${tenantId}/manage/lms/awards`, label: "Awards", permission: "page_lms_awards" },
              ],
            },
          ]
        : [
            {
              to: `/${tenantId}/manage/training/athletes`,
              label: "Training Programs",
              permission: "page_training_athletes",
            },
          ]),
      ...(settings?.athlete_monitoring_enabled
        ? [
            {
              to: `/${tenantId}/manage/monitoring`,
              label: "Progress Overview",
              permission: "page_monitoring",
            },
            {
              to: `/${tenantId}/manage/monitoring/skills`,
              label: "Manage Skills",
              permission: "page_monitoring_skills",
            },
          ]
        : []),
    ],
  };

  const attendance: NavSectionConfig = {
    title: "Attendance",
    sectionKey: "attendance",
    icon: <ClipboardCheck className={iconCls} />,
    directLink: `/${tenantId}/manage/attendance`,
    items: [
      {
        to: `/${tenantId}/manage/attendance`,
        label: "Mark Attendance",
        permission: "mark_attendance_page",
      },
      {
        to: `/${tenantId}/manage/attendance/absences`,
        label: "Recent Absence",
        permission: "mark_attendance_page",
      },
      {
        to: `/${tenantId}/manage/attendance/notes`,
        label: "Attendance Notes",
        permission: "mark_attendance_page",
      },
      {
        to: `/${tenantId}/manage/reports/incident-reports`,
        label: "Incident Reports",
        permission: "view_incident_reports",
      },
    ],
  };

  const tasks: NavSectionConfig = {
    title: "My Tasks",
    sectionKey: "tasks",
    icon: <ListChecks className={iconCls} />,
    directLink: `/${tenantId}/manage/tasks`,
    isDirectLink: true,
    items: [
      { to: `/${tenantId}/manage/tasks`, label: "View My Tasks", permission: "page_tasks" },
    ],
  };

  const programs: NavSectionConfig = {
    title: "Programs",
    sectionKey: "programs",
    // Section visibility = section_programs + union of its (page-permission
    // gated) children; every child is grant-on-request, so staff see nothing
    // here until a page permission is granted. Admins bypass.
    icon: <LayoutGrid className={iconCls} />,
    directLink: `/${tenantId}/manage/programs`,
    items: [
      {
        to: `/${tenantId}/manage/programs/create`,
        label: "Create Programs",
        permission: "page_programs",
      },
      {
        to: `/${tenantId}/manage/programs`,
        label: "Manage Programs",
        permission: "page_programs",
      },
      {
        to: `/${tenantId}/manage/programs/categories`,
        label: "Manage Categories",
        permission: "page_programs_categories",
      },
      {
        to: `/${tenantId}/manage/private-sessions`,
        label: "Private Sessions",
        permission: "page_private_sessions",
      },
    ],
  };

  const events: NavSectionConfig = {
    title: "Events",
    sectionKey: "events",
    icon: <Calendar className={iconCls} />,
    directLink: `/${tenantId}/manage/events`,
    items: [
      { to: `/${tenantId}/manage/events`, label: "All Events", permission: "page_events" },
      {
        to: `/${tenantId}/manage/events/templates`,
        label: "Templates",
        permission: "page_events_templates",
      },
      {
        to: `/${tenantId}/manage/events/analytics`,
        label: "Analytics",
        permission: "page_events_analytics",
      },
      {
        to: `/${tenantId}/manage/events/birthday-parties`,
        label: "Birthday Parties",
        permission: "page_events_birthday_parties",
      },
      { to: `/${tenantId}/manage/camps`, label: "Camps", permission: "page_camps" },
      { to: `/${tenantId}/manage/clinics`, label: "Clinics", permission: "page_clinics" },
    ],
  };

  const store: NavSectionConfig = {
    title: "Store",
    sectionKey: "store",
    icon: <ShoppingBag className={iconCls} />,
    directLink: `/${tenantId}/admin/merchandise`,
    items: [
      // Merchandise/Equipment point at legacy /admin/* routes (not part of
      // the /manage page-permission conversion) — left ungated.
      { to: `/${tenantId}/admin/merchandise`, label: "Merchandise" },
      { to: `/${tenantId}/admin/equipment`, label: "Equipment" },
      { to: `/${tenantId}/manage/store/music`, label: "Music", permission: "page_store_music" },
      {
        to: `/${tenantId}/manage/store/pos`,
        label: "Point of Sale",
        permission: "page_store_pos",
      },
      {
        to: `/${tenantId}/manage/store/orders`,
        label: "Orders",
        permission: "page_store_orders",
      },
      {
        to: `/${tenantId}/manage/settings/pos`,
        label: "Storefront, POS & Shipping",
        permission: "page_settings_pos",
      },
    ],
  };

  const marketing: NavSectionConfig = {
    title: "Marketing",
    sectionKey: "marketing",
    icon: <Megaphone className={iconCls} />,
    directLink: `/${tenantId}/manage/marketing/broadcasts`,
    items: [
      {
        label: "Broadcasts",
        isNested: true,
        navKey: "marketing_broadcasts",
        items: [
          {
            to: `/${tenantId}/manage/marketing/broadcasts`,
            label: "All Broadcasts",
            permission: "page_marketing_broadcasts",
          },
          {
            to: `/${tenantId}/manage/marketing/broadcasts/new`,
            label: "New Broadcast",
            permission: "group_email",
          },
        ],
      },
      {
        label: "Posts",
        isNested: true,
        navKey: "marketing_posts",
        // Group visibility = union of its permission-gated children.
        items: [
          {
            to: `/${tenantId}/manage/marketing/posts`,
            label: "All Posts",
            permission: "page_marketing_posts",
          },
          {
            to: `/${tenantId}/manage/marketing/posts/new`,
            label: "New Post",
            permission: "page_marketing_posts",
          },
          {
            to: `/${tenantId}/manage/marketing/posts/categories`,
            label: "Categories",
            permission: "page_marketing_posts_categories",
          },
        ],
      },
      {
        label: "Social",
        to: `/${tenantId}/manage/marketing/social`,
        permission: "page_marketing_social",
      },
      {
        label: "Local SEO",
        to: `/${tenantId}/manage/marketing/local-seo`,
        permission: "page_marketing_local_seo",
        moduleKey: "marketing_growth_suite",
      },
      {
        label: "Advertising Intelligence",
        to: `/${tenantId}/manage/marketing/advertising-intelligence`,
        permission: "page_marketing_advertising_intelligence",
        moduleKey: "marketing_growth_suite",
      },
      {
        label: "Reputation AI",
        to: `/${tenantId}/manage/marketing/reputation`,
        permission: "page_marketing_reputation",
        moduleKey: "marketing_growth_suite",
      },
      {
        label: "Analytics",
        to: `/${tenantId}/manage/marketing/analytics`,
        permission: "page_marketing_analytics",
      },
      {
        label: "Campaigns",
        to: `/${tenantId}/manage/marketing/campaigns`,
        permission: "page_marketing_campaigns",
        moduleKey: "marketing_communications",
      },
      {
        label: "Design Lab",
        to: `/${tenantId}/manage/design-lab`,
        permission: "page_design_lab",
      },
      {
        label: "Discount Codes",
        to: `/${tenantId}/manage/marketing/discount-codes`,
        // The same permission the discount code API checks.
        permission: "manage_discount_codes",
      },
      {
        label: "Surveys",
        to: `/${tenantId}/manage/marketing/surveys`,
        permission: "page_marketing_surveys",
        moduleKey: "surveys_and_forms",
      },
      {
        to: `/${tenantId}/manage/reports/birthdays`,
        label: `${participantSingular} Birthdays`,
        permission: "page_reports_birthdays",
      },
    ],
  };

  const crm: NavSectionConfig = {
    title: "CRM",
    sectionKey: "crm",
    icon: <Contact className={iconCls} />,
    directLink: `/${tenantId}/manage/crm/leads`,
    items: [
      { to: `/${tenantId}/manage/crm/leads`, label: "Lead Pipeline", permission: "page_crm_leads" },
      { to: `/${tenantId}/manage/crm/funnel`, label: "Funnel Report", permission: "page_crm_funnel" },
      { to: `/${tenantId}/manage/crm/inbox`, label: "Unified Inbox", permission: "page_crm_inbox" },
      { to: `/${tenantId}/manage/customers`, label: "Customers", permission: "page_customers" },
      {
        to: `/${tenantId}/manage/marketing/contacts`,
        label: "Contacts",
        permission: "page_marketing_contacts",
      },
      {
        to: `/${tenantId}/manage/crm/reviews`,
        label: "Review Requests",
        permission: "page_crm_reviews",
      },
    ],
  };

  const billing: NavSectionConfig = {
    title: "Billing",
    sectionKey: "billing",
    icon: <Receipt className={iconCls} />,
    directLink: `/${tenantId}/manage/billing/invoice/new`,
    items: [
      {
        to: `/${tenantId}/manage/billing/invoice/new`,
        label: "New Invoice",
        permission: "generate_invoice_page",
      },
      {
        to: `/${tenantId}/manage/reports/members`,
        label: "Monthly Dues",
        permission: "page_reports_members",
      },
      {
        to: `/${tenantId}/manage/reports/billing-run`,
        label: "Billing Run",
        permission: "view_financial_reports",
      },
      {
        to: `/${tenantId}/manage/reports/billing-reconciliation`,
        label: "Billing Reconciliation",
        permission: "page_reports_billing_reconciliation",
      },
      {
        to: `/${tenantId}/manage/reports/annual-registration-fees`,
        label: "Annual Registration Fees",
        permission: "page_reports_annual_registration_fees",
      },
      {
        to: `/${tenantId}/manage/billing/competition-fees`,
        label: "Competition Fees",
        permission: "manage_invoices",
      },
      {
        to: `/${tenantId}/manage/reports/balances`,
        label: "Negative Balances",
        adminOnly: true,
      },
    ],
  };

  // Reports: single link to the landing page. The old 20+-item
  // accordion (5 categories) lives there now, with client-side
  // filtering. adminOnly because every report it contains was.
  const reports: NavSectionConfig = {
    title: "Reports",
    sectionKey: "reporting",
    // adminOnly stays: this is a direct-link section, so union-of-children
    // gating can't apply (the link always renders). Revisit when section
    // gating moves to page permissions.
    adminOnly: true,
    icon: <BarChart3 className={iconCls} />,
    directLink: `/${tenantId}/manage/reports`,
    isDirectLink: true,
    items: [
      { to: `/${tenantId}/manage/reports`, label: "Reports", permission: "page_reports" },
    ],
  };

  const staff: NavSectionConfig = {
    title: "Staff",
    sectionKey: "staff",
    // Section visibility = section_staff + union of its page-permission
    // gated children (all grant-on-request). Admins bypass.
    icon: <UserCog className={iconCls} />,
    directLink: `/${tenantId}/manage/staff`,
    items: [
      { to: `/${tenantId}/manage/staff/add`, label: "Add New Staff", permission: "page_staff" },
      { to: `/${tenantId}/manage/staff`, label: "Manage", permission: "page_staff" },
      {
        to: `/${tenantId}/manage/staff/training`,
        label: "Training Programs",
        permission: "page_staff_training",
      },
    ],
  };

  const aiReceptionist: NavSectionConfig = {
    title: "AI Receptionist",
    sectionKey: "ai_receptionist",
    // Section visibility = section_ai_receptionist + union of its
    // page-permission gated children (all grant-on-request). Admins bypass.
    moduleKey: "ai_phone_receptionist",
    icon: <Bot className={iconCls} />,
    directLink: `/${tenantId}/manage/ai-receptionist`,
    items: [
      {
        to: `/${tenantId}/manage/ai-receptionist`,
        label: "Configuration",
        permission: "page_ai_receptionist",
      },
      {
        to: `/${tenantId}/manage/ai-receptionist/knowledge`,
        label: "Knowledge Base",
        permission: "page_ai_receptionist_knowledge",
      },
      {
        to: `/${tenantId}/manage/ai-receptionist/conversations`,
        label: "Conversations",
        permission: "page_ai_receptionist_conversations",
      },
      {
        to: `/${tenantId}/manage/ai-receptionist/analytics`,
        label: "Analytics",
        permission: "page_ai_receptionist_analytics",
      },
    ],
  };

  // Live call routing for the AI receptionist. No section_ permission: each
  // item carries its own (call history / routing settings), and the
  // section disappears when neither is held.
  const calls: NavSectionConfig = {
    title: "Calls",
    sectionKey: "calls",
    skipSectionPermission: true,
    moduleKey: "ai_phone_receptionist",
    icon: <PhoneCall className={iconCls} />,
    directLink: `/${tenantId}/manage/calls`,
    items: [
      {
        to: `/${tenantId}/manage/calls`,
        label: "Call History & Messages",
        permission: "view_call_history",
      },
      {
        to: `/${tenantId}/manage/ai-receptionist/call-routing`,
        label: "Call Routing",
        permission: "manage_call_routing",
      },
    ],
  };

  const hr: NavSectionConfig = {
    title: "HR Management",
    sectionKey: "hr_management",
    // Section visibility = section_hr_management + union of its
    // page-permission gated children (all grant-on-request). Admins bypass.
    icon: <Briefcase className={iconCls} />,
    directLink: `/${tenantId}/manage/hr/employees`,
    items: [
      {
        to: `/${tenantId}/manage/hr/employees`,
        label: "Employees",
        permission: "page_hr_employees",
      },
      {
        to: `/${tenantId}/manage/hr/time-tracking`,
        label: "Time Tracking",
        permission: "page_hr_time_tracking",
      },
      {
        to: `/${tenantId}/manage/hr/onboarding`,
        label: "Onboarding",
        permission: "page_hr_onboarding",
      },
      {
        to: `/${tenantId}/manage/hr/documents`,
        label: "Policies & SOPs",
        permission: "page_hr_documents",
      },
      {
        to: `/${tenantId}/manage/hr/payroll`,
        label: "Payroll",
        permission: "page_hr_payroll",
        comingSoon: true,
      },
    ],
  };

  // Settings: single link to the searchable settings landing page —
  // replaces the 27-item accordion. adminOnly as before.
  const settingsSection: NavSectionConfig = {
    title: "Settings",
    sectionKey: "settings",
    // adminOnly stays: direct-link section — union-of-children gating
    // can't apply (the link always renders). Revisit with section gating.
    adminOnly: true,
    icon: <SettingsIcon className={iconCls} />,
    directLink: `/${tenantId}/manage/settings/all`,
    isDirectLink: true,
    items: [
      {
        to: `/${tenantId}/manage/settings/all`,
        label: "Settings",
        permission: "page_settings_all",
      },
    ],
  };

  // ── The consolidated rail (Dan 2026-10-31) ──────────────────────
  // {participants}: the roster pages go to CRM, training to Programs.
  const ATHLETE_ROSTER_PAGES = 4; // Manage, Pending Reactivations, Teams, Levels
  const reviewRequests = crm.items.filter((i) => "to" in i && i.to.endsWith("/crm/reviews"));
  const crmPages = crm.items.filter((i) => !reviewRequests.includes(i));

  const home = mergeSections(
    { title: "Home", sectionKey: "home", skipSectionPermission: true, icon: <LayoutDashboard className={iconCls} />, directLink: dashboard.directLink },
    [
      { section: dashboard },
      { section: tasks, items: tasks.items.map((i) => ("to" in i ? { ...i, label: "My Tasks" } : i)) },
    ]
  );

  const people = mergeSections(
    { title: "CRM", sectionKey: "crm", skipSectionPermission: true, icon: <Contact className={iconCls} />, directLink: memberships.directLink },
    [
      { label: "Members", section: memberships },
      { label: participantPlural, section: athletes, items: athletes.items.slice(0, ATHLETE_ROSTER_PAGES) },
      { label: "Leads & Contacts", section: crm, items: crmPages },
    ]
  );

  const programsAndTraining = mergeSections(
    { title: "Programs", sectionKey: "programs", skipSectionPermission: true, icon: programs.icon, directLink: programs.directLink },
    [
      { section: programs },
      { label: "Training", section: athletes, items: flattenNested(athletes.items.slice(ATHLETE_ROSTER_PAGES)) },
    ]
  );

  const marketingAndReviews = mergeSections(
    { title: "Marketing", sectionKey: "marketing", skipSectionPermission: true, icon: marketing.icon, directLink: marketing.directLink },
    [{ section: marketing }, { section: crm, items: reviewRequests }]
  );

  const team = mergeSections(
    { title: "Team", sectionKey: "team", skipSectionPermission: true, icon: <UserCog className={iconCls} />, directLink: staff.directLink },
    [
      { label: "Staff", section: staff },
      { label: "HR", section: hr },
    ]
  );

  const phone = mergeSections(
    {
      title: "Phone",
      sectionKey: "phone",
      skipSectionPermission: true,
      icon: <PhoneCall className={iconCls} />,
      directLink: calls.directLink,
      moduleKey: "ai_phone_receptionist",
    },
    [
      { label: "Calls", section: calls },
      { label: "AI Receptionist", section: aiReceptionist },
    ]
  );

  return [
    { label: null, sections: [home] },
    { label: "People", sections: [people, attendance] },
    { label: "Programs & Events", sections: [programsAndTraining, events] },
    { label: "Commerce", sections: [billing, store, marketingAndReviews] },
    { label: "Insights", sections: [reports] },
    { label: "Organization", sections: [team, phone, settingsSection] },
  ];
}

export interface MemberMenuContext {
  tenantId: string | null;
  /** i18next TFunction — typed loosely so any t-shape plugs in */
  t: (key: string, opts?: any) => any;
  facilityVisible: boolean;
  storefrontVisible: boolean;
}

export interface MemberMenuSection {
  title: string;
  navKey: string;
  items: Array<{ to: string; label: string }>;
}

export function buildMemberMenus(ctx: MemberMenuContext): {
  sections: MemberMenuSection[];
  generalLinks: Array<{ to: string; label: string; hidden?: boolean }>;
} {
  const { tenantId, t, facilityVisible, storefrontVisible } = ctx;
  return {
    sections: [
      {
        title: t("nav.events", { defaultValue: "Events" }),
        navKey: "member_events",
        items: [
          { to: `/${tenantId}/member/events`, label: t("nav.eventsList", { defaultValue: "Browse Events" }) },
          { to: `/${tenantId}/member/my-events`, label: t("nav.myEvents", { defaultValue: "My Events" }) },
          { to: `/${tenantId}/member/calendar`, label: t("nav.calendar", { defaultValue: "Calendar" }) },
        ],
      },
      {
        title: t("nav.camps", { defaultValue: "Camps" }),
        navKey: "member_camps",
        items: [
          { to: `/${tenantId}/member/camps`, label: t("nav.browseCamps", { defaultValue: "Browse Camps" }) },
          { to: `/${tenantId}/member/my-camps`, label: t("nav.myCamps", { defaultValue: "My Camps" }) },
          { to: `/${tenantId}/member/camp-calendar`, label: t("nav.campCalendar", { defaultValue: "Calendar" }) },
        ],
      },
      {
        title: t("nav.clinics", { defaultValue: "Clinics" }),
        navKey: "member_clinics",
        items: [
          { to: `/${tenantId}/member/clinics`, label: t("nav.browseClinics", { defaultValue: "Browse Clinics" }) },
          { to: `/${tenantId}/member/my-clinics`, label: t("nav.myClinics", { defaultValue: "My Clinics" }) },
          { to: `/${tenantId}/member/clinic-calendar`, label: t("nav.clinicCalendar", { defaultValue: "Calendar" }) },
        ],
      },
    ],
    generalLinks: [
      { to: `/${tenantId}/member/view-billing`, label: t("nav.billing", { defaultValue: "Billing" }) },
      { to: `/${tenantId}/member/manage-profile`, label: t("nav.profile") },
      { to: `/${tenantId}/member/manage-athlete`, label: t("nav.athletes") },
      { to: `/${tenantId}/member/facilities`, label: "Book a facility", hidden: !facilityVisible },
      { to: `/${tenantId}/member/facilities/bookings`, label: "My bookings", hidden: !facilityVisible },
      { to: `/${tenantId}/member/store`, label: t("nav.store"), hidden: !storefrontVisible },
      { to: `/${tenantId}/member/feedback`, label: t("nav.feedback") },
      { to: `/${tenantId}/member/help`, label: t("nav.help", { defaultValue: "Help" }) },
      { to: `/${tenantId}/member/contact`, label: t("nav.support") },
    ],
  };
}

/** Active-route helpers shared by the section renderer. */
export const isItemActive = (pathname: string, to?: string) =>
  !!to && pathname === to;

export const isSectionActive = (pathname: string, section: NavSectionConfig) => {
  if (isItemActive(pathname, section.directLink)) return true;
  return section.items.some((item) => {
    if ("isCategory" in item) return false;
    if ("isNested" in item) return item.items.some((s) => isItemActive(pathname, s.to));
    return isItemActive(pathname, item.to);
  });
};
